import { useState, useEffect } from "react";
import { functions } from "../../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useDarkMode } from "../../hooks/useDarkMode";
import { FiTrendingUp, FiAlertCircle, FiCheck, FiInfo } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const UsageStatsWidget = ({ compact = false }) => {
  const { userData } = useAuth();
  const { t }        = useLanguage();
  const { darkMode } = useDarkMode();
  const navigate     = useNavigate();
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (userData?.tenantId) loadUsageStats();
    else setLoading(false);
  }, [userData]);

  const loadUsageStats = async () => {
    try {
      setLoading(true); setError("");
      const getUsageStats = httpsCallable(functions, "getUsageStats");
      const result = await getUsageStats({ tenantId: userData.tenantId });
      setStats(result.data);
    } catch { setError("silent"); }
    finally { setLoading(false); }
  };

  /* ── Shared helpers ──────────────────────────────────── */
  const cardCls    = `border rounded-xl ${darkMode ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-200 shadow-sm"}`;
  const labelCls   = darkMode ? "text-gray-300" : "text-gray-700";
  const subCls     = darkMode ? "text-gray-400" : "text-gray-500";
  const valueCls   = darkMode ? "text-white"    : "text-gray-900";
  const trackCls   = darkMode ? "bg-gray-700"   : "bg-gray-200";
  const dividerCls = darkMode ? "border-gray-700" : "border-gray-200";

  const getProgressColor = (pct) =>
    pct >= 90 ? "bg-red-500" : pct >= 80 ? "bg-yellow-500" : "bg-blue-500";

  const getAlertColor = (pct) =>
    pct >= 90
      ? "text-red-400 bg-red-500/20"
      : "text-yellow-400 bg-yellow-500/20";

  /* ── Loading skeleton ────────────────────────────────── */
  if (loading) return (
    <div className={`${cardCls} p-6`}>
      <div className="animate-pulse">
        <div className={`h-4 ${darkMode ? "bg-gray-700" : "bg-gray-200"} rounded w-1/3 mb-4`} />
        <div className={`h-20 ${darkMode ? "bg-gray-700" : "bg-gray-200"} rounded`} />
      </div>
    </div>
  );

  if (error === "silent" || !stats) return null;

  const evPct       = parseFloat(stats.percentage?.evaluations || 0);
  const isUnlimited = stats.quotas?.evaluations === -1;
  const current     = stats.usage?.evaluations || 0;
  const quota       = stats.quotas?.evaluations || 0;

  /* ══ COMPACT MODE ════════════════════════════════════════ */
  if (compact) return (
    <div className={`${cardCls} px-4 py-3 flex flex-wrap items-center gap-4`}>

      {/* Title */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <FiTrendingUp className="text-blue-400 text-sm" />
        <span className={`${labelCls} text-xs font-medium`}>{t("usage_this_month")}</span>
        <span className={`${subCls} text-xs`}>{stats.period || ""}</span>
      </div>

      {/* Evaluations bar */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className={`${subCls} text-xs flex-shrink-0`}>{t("campaign_evaluations")}</span>
        <div className={`flex-1 min-w-16 max-w-32 ${trackCls} rounded-full h-1.5`}>
          {!isUnlimited && quota > 0
            ? <div className={`h-1.5 rounded-full transition-all ${getProgressColor(evPct)}`} style={{ width: `${Math.min(evPct, 100)}%` }} />
            : <div className="h-1.5 rounded-full bg-green-500 w-full" />
          }
        </div>
        <span className={`${valueCls} text-xs font-semibold flex-shrink-0`}>
          {current}{!isUnlimited ? ` / ${quota}` : ` / ∞`}
        </span>
        {evPct >= 90 && <FiAlertCircle className="text-red-400 text-xs flex-shrink-0" />}
      </div>

      {/* Shares */}
      {stats.usage?.shares !== undefined && (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className={`${subCls} text-xs`}>{t("shares_created")}:</span>
          <span className={`${valueCls} text-xs font-semibold`}>{stats.usage.shares}</span>
        </div>
      )}

      {/* PDFs */}
      {stats.usage?.pdfs !== undefined && (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className={`${subCls} text-xs`}>{t("pdfs_generated")}:</span>
          <span className={`${valueCls} text-xs font-semibold`}>{stats.usage.pdfs}</span>
        </div>
      )}

      {/* Upgrade CTA */}
      {!isUnlimited && evPct >= 80 && (
        <button
          onClick={() => navigate("/admin/plans")}
          className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-lg text-xs font-medium transition-colors"
        >
          {t("view_plans")}
        </button>
      )}
    </div>
  );

  /* ══ FULL MODE ═══════════════════════════════════════════ */
  return (
    <div className={`${cardCls} p-6`}>

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FiTrendingUp className="text-blue-400 text-xl" />
          <h3 className={`${valueCls} font-semibold`}>{t("usage_this_month")}</h3>
        </div>
        <span className={`${subCls} text-sm`}>{stats.period || ""}</span>
      </div>

      {/* Evaluations section */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className={`${labelCls} text-sm`}>{t("campaign_evaluations")}</span>
          <span className={`${valueCls} font-semibold`}>
            {current}
            {!isUnlimited && ` / ${quota}`}
            {isUnlimited  && ` / ${t("unlimited")}`}
          </span>
        </div>

        {!isUnlimited && quota > 0 && (
          <>
            <div className={`w-full ${trackCls} rounded-full h-2 mb-2`}>
              <div
                className={`h-2 rounded-full transition-all ${getProgressColor(evPct)}`}
                style={{ width: `${Math.min(evPct, 100)}%` }}
              />
            </div>

            {/* Warning / alert banner */}
            {evPct >= 80 && (
              <div className={`flex items-center gap-2 ${getAlertColor(evPct)} p-2 rounded-xl mt-2`}>
                <FiAlertCircle className="flex-shrink-0" />
                <p className="text-xs">{evPct >= 90 ? t("almost_at_limit") : t("approaching_limit")}</p>
              </div>
            )}

            {/* Zero usage hint */}
            {evPct < 80 && current === 0 && (
              <div className="flex items-center gap-2 text-blue-400 bg-blue-500/10 p-2 rounded-xl mt-2">
                <FiInfo className="flex-shrink-0" />
                <p className="text-xs">{quota} {t("evaluations_available")}</p>
              </div>
            )}
          </>
        )}

        {/* Unlimited badge */}
        {isUnlimited && (
          <div className="flex items-center gap-2 text-green-400 bg-green-500/10 p-2 rounded-xl">
            <FiCheck />
            <p className="text-xs">{t("unlimited_evaluations")}</p>
          </div>
        )}
      </div>

      {/* Shares + PDFs row */}
      {(stats.usage?.shares !== undefined || stats.usage?.pdfs !== undefined) && (
        <div className={`grid grid-cols-2 gap-4 pt-4 border-t ${dividerCls}`}>
          {stats.usage?.shares !== undefined && (
            <div>
              <p className={`${subCls} text-xs mb-1`}>{t("shares_created")}</p>
              <p className={`${valueCls} font-semibold`}>
                {stats.usage.shares}
                {stats.quotas?.shares !== -1 && stats.quotas?.shares ? ` / ${stats.quotas.shares}` : ""}
              </p>
            </div>
          )}
          {stats.usage?.pdfs !== undefined && (
            <div>
              <p className={`${subCls} text-xs mb-1`}>{t("pdfs_generated")}</p>
              <p className={`${valueCls} font-semibold`}>
                {stats.usage.pdfs}
                {stats.quotas?.pdfs !== -1 && stats.quotas?.pdfs ? ` / ${stats.quotas.pdfs}` : ""}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Upgrade button */}
      {!isUnlimited && evPct >= 80 && (
        <div className={`mt-4 pt-4 border-t ${dividerCls}`}>
          <button
            onClick={() => navigate("/admin/plans")}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-xl text-sm font-semibold transition-colors"
          >
            {t("view_plans")}
          </button>
        </div>
      )}
    </div>
  );
};

export default UsageStatsWidget;